// src/components/Navbar.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiMenu, FiX, FiUser, FiChevronDown } from 'react-icons/fi';
import './Navbar.css';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useSelectedState } from '../contexts/SelectedStateContext';

const logo = '/images/9ac3ab10-e0dd-4fbf-9b7c-e5717f79bc1b.png';
const appIcon = '/android-chrome-192x192.png';

// Dropdown nav item — hover-opens on desktop; on mobile it's a tap-to-expand
// accordion/disclosure (the standard pattern for off-canvas mobile menus,
// since hover flyouts don't work on touch).
function NavDropdown({ menuKey, label, active, links, isActive, closeMenu, openMenu, onToggle }) {
  const isOpen = openMenu === menuKey;
  return (
    <li className={`nav-dropdown${active ? ' nav-dropdown--active' : ''}`}>
      <button
        className={`nav-dropdown-trigger${active ? ' active' : ''}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={() => onToggle(menuKey)}
      >
        {label} <FiChevronDown className={`nav-dropdown-chevron${isOpen ? ' rotated' : ''}`} />
      </button>
      <ul className={`nav-dropdown-list${isOpen ? ' nav-dropdown-list--open' : ''}`}>
        {links.map(({ to, base, label: linkLabel }) => (
          <li key={base}>
            <Link to={to} onClick={closeMenu} className={isActive(base) ? 'active' : ''}>
              {linkLabel}
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
}

const NAV_STATES_LIST = [
  { code: 'AL', name: 'Alabama' },       { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },       { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },    { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },   { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'D.C.' },          { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },       { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },         { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },       { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },        { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },     { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },      { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },      { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },   { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },       { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },        { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },    { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },      { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },      { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },{ code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },     { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },          { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },      { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { subscription } = useSubscription();
  const hasActiveSubscription = Boolean(user) && Boolean(subscription?.isActive);
  const { selectedState: currentState, updateState } = useSelectedState();
  const [open, setOpen] = useState(false);
  // Which mobile accordion dropdown (if any) is expanded — only one at a time.
  const [openMobileMenu, setOpenMobileMenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState('');
  const stateSelectorRef = useRef(null);

  // Collapse any open accordion whenever the mobile drawer itself closes.
  useEffect(() => {
    if (!open) setOpenMobileMenu(null);
  }, [open]);

  // Close state dropdown on outside click
  useEffect(() => {
    if (!stateDropdownOpen) return;
    const onClickOutside = (e) => {
      if (stateSelectorRef.current && !stateSelectorRef.current.contains(e.target)) {
        setStateDropdownOpen(false);
        setStateSearch('');
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [stateDropdownOpen]);

  const handleStateChange = (code) => {
    updateState(code);
    setStateDropdownOpen(false);
    setStateSearch('');
    const p = location.pathname;
    if (p === '/' || /^\/[a-z]{2}$/i.test(p)) {
      navigate(`/${code.toLowerCase()}`);
    } else if (p.startsWith('/value-finder')) {
      navigate(`/value-finder/${code.toLowerCase()}`);
    } else if (p.startsWith('/promo-finder')) {
      navigate(`/promo-finder/${code.toLowerCase()}`);
    } else if (p.startsWith('/promotions')) {
      navigate(`/promotions/${code.toLowerCase()}`);
    }
  };

  const st = currentState ? currentState.toLowerCase() : '';

  // Build state-aware hrefs for the pages that carry a /:state segment.
  const homeHref        = st ? `/${st}` : '/';
  const bestBetsHref    = st ? `/value-finder/${st}` : '/value-finder';
  const promoFinderHref = st ? `/promo-finder/${st}` : '/promo-finder';
  const promotionsHref  = st ? `/promotions/${st}` : '/promotions';

  // Active detection: match the base path regardless of state suffix.
  // e.g. /value-finder/fl is "active" for the Value Finder link.
  const isActive = (basePath) => {
    const p = location.pathname;
    if (basePath === '/') {
      // Home: exact / or any /:stateCode (exactly two letters)
      return p === '/' || /^\/[a-z]{2}$/i.test(p);
    }
    return p === basePath || p.startsWith(`${basePath}/`);
  };

  const isPromoActive =
    location.pathname.startsWith('/promotions') ||
    location.pathname.startsWith('/promo-finder');

  const isBettingToolsActive =
    isActive('/value-finder') || isActive('/parlay-picker') || isActive('/calculators');

  const isEducationActive = isActive('/academy');

  const bettingToolsLinks = [
    { to: bestBetsHref, base: '/value-finder', label: 'Value Finder' },
    { to: '/parlay-picker', base: '/parlay-picker', label: 'Parlay Picker' },
    { to: '/calculators', base: '/calculators', label: 'Calculators' },
  ];

  const educationLinks = [
    { to: '/academy', base: '/academy', label: 'Academy' },
    { to: '/academy/ev-betting', base: '/academy/ev-betting', label: 'Expected Value Explained' },
  ];

  const promoLinks = [
    { to: promoFinderHref, base: '/promo-finder', label: 'Promotion Calculator' },
    { to: promotionsHref,  base: '/promotions',   label: 'Promotion Codes' },
  ];

  const toggleMenu = () => setOpen(o => !o);
  const closeMenu = () => setOpen(false);
  const toggleMobileMenu = (key) => setOpenMobileMenu(prev => (prev === key ? null : key));

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <button className="hamburger" onClick={toggleMenu} aria-label="Toggle menu">
            {open ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>

          <div className="navbar-left">
            <div className="logo-container">
              <Link to={homeHref} onClick={closeMenu}>
                <img src={logo} alt="BoostYourBets" className="logo-container-img" />
              </Link>
            </div>

            {/* State selector — always visible on all screen sizes */}
            <div className="navbar-state-selector" ref={stateSelectorRef}>
              <button
                className={`navbar-state-btn${stateDropdownOpen ? ' navbar-state-btn--open' : ''}`}
                onClick={() => setStateDropdownOpen(prev => !prev)}
                aria-haspopup="listbox"
                aria-expanded={stateDropdownOpen}
                aria-label={`Selected state: ${currentState || 'FL'}. Click to change.`}
              >
                <svg className="navbar-state-btn__pin" width="13" height="16" viewBox="0 0 11 14" fill="none" aria-hidden="true">
                  <path d="M5.5 0C3.01 0 1 2.01 1 4.5c0 3.375 4.5 9.5 4.5 9.5s4.5-6.125 4.5-9.5C10 2.01 7.99 0 5.5 0zm0 6.125a1.625 1.625 0 1 1 0-3.25 1.625 1.625 0 0 1 0 3.25z" fill="#32CD32"/>
                </svg>
                <span className="navbar-state-btn__label">{currentState || 'FL'}</span>
                <svg
                  className={`navbar-state-btn__chevron${stateDropdownOpen ? ' navbar-state-btn__chevron--open' : ''}`}
                  width="12" height="7" viewBox="0 0 10 6" fill="none" aria-hidden="true"
                >
                  <path d="M1 1l4 4 4-4" stroke="#32CD32" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {stateDropdownOpen && (
                <div className="navbar-state-dropdown" role="listbox" aria-label="Select your state">
                  <div className="navbar-state-dropdown__search-wrap">
                    <input
                      className="navbar-state-dropdown__search"
                      type="text"
                      placeholder="Search state…"
                      value={stateSearch}
                      onChange={e => setStateSearch(e.target.value)}
                      autoFocus
                      aria-label="Search states"
                    />
                  </div>
                  <div className="navbar-state-dropdown__list">
                    {NAV_STATES_LIST
                      .filter(s =>
                        !stateSearch ||
                        s.name.toLowerCase().includes(stateSearch.toLowerCase()) ||
                        s.code.toLowerCase().startsWith(stateSearch.toLowerCase())
                      )
                      .map(s => (
                        <button
                          key={s.code}
                          className={`navbar-state-option${currentState === s.code ? ' navbar-state-option--active' : ''}`}
                          role="option"
                          aria-selected={currentState === s.code}
                          onClick={() => handleStateChange(s.code)}
                        >
                          <span className="navbar-state-option__code">{s.code}</span>
                          <span className="navbar-state-option__name">{s.name}</span>
                        </button>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
          </div>

          <ul className={`nav-links ${open ? 'open' : ''}`}>
            {/* Close button inside menu - mobile only */}
            

            {/* Betting Tools dropdown — hover-opens on desktop, tap-accordion on mobile */}
            <NavDropdown
              menuKey="bettingTools"
              label="Betting Tools"
              active={isBettingToolsActive}
              links={bettingToolsLinks}
              isActive={isActive}
              closeMenu={closeMenu}
              openMenu={openMobileMenu}
              onToggle={toggleMobileMenu}
            />

            <li>
              <Link
                to="/nfl"
                onClick={closeMenu}
                className={isActive('/nfl') ? 'active' : ''}
              >
                NFL
              </Link>
            </li>

            {/* Education dropdown — hover-opens on desktop, tap-accordion on mobile */}
            <NavDropdown
              menuKey="education"
              label="Education"
              active={isEducationActive}
              links={educationLinks}
              isActive={isActive}
              closeMenu={closeMenu}
              openMenu={openMobileMenu}
              onToggle={toggleMobileMenu}
            />

            {/* Promotions dropdown — hover-opens on desktop, tap-accordion on mobile */}
            <NavDropdown
              menuKey="promotions"
              label="Promotions"
              active={isPromoActive}
              links={promoLinks}
              isActive={isActive}
              closeMenu={closeMenu}
              openMenu={openMobileMenu}
              onToggle={toggleMobileMenu}
            />

            <li>
              <Link
                to="/analysis"
                onClick={closeMenu}
                className={isActive('/analysis') ? 'active' : ''}
              >
                Analysis
              </Link>
            </li>

            <li>
              <Link
                to="/results"
                onClick={closeMenu}
                className={isActive('/results') ? 'active' : ''}
              >
                Results
              </Link>
            </li>

            <li>
              <Link
                to="/community"
                onClick={closeMenu}
                className={isActive('/community') ? 'active' : ''}
              >
                Community
              </Link>
            </li>

            {/* auth section: logged-in + subscribed users see Account; everyone else sees Subscribe */}
            {hasActiveSubscription ? (
              <li className="nav-right">
                <Link to="/subscribe" className="account-button" onClick={closeMenu}>
                  <FiUser />
                  <span className="account-button__label-desktop">Account</span>
                  <span className="account-button__label-mobile">{user.email}</span>
                </Link>
              </li>
            ) : (
              <li className="nav-right">
                <Link
                  to="/subscribe"
                  onClick={closeMenu}
                  className={`nav-cta${location.pathname === '/subscribe' ? ' active' : ''}`}
                >Subscribe</Link>
              </li>
            )}
          </ul>
        </div>
      </nav>
      
      {/* promo-bar hidden — football season promotion ended */}

      {open && <div className="overlay" onClick={closeMenu} />}
    </>
  );
}

